import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { LayoutDashboard, Users, FolderTree, Package, ClipboardList, Wallet, BadgeCheck, Target, FileText, TrendingUp, AlertCircle, CheckCircle2, XCircle, Copy, UserCircle2, Building2, Bell, Pencil, Eye, EyeOff, KeyRound, HeadphonesIcon, QrCode, IndianRupee, User as UserIcon, Filter } from "lucide-react";
import DashboardLayout, { KpiCard } from "@/components/DashboardLayout";
import { api, formatError } from "@/lib/api";
import { StatusBadge, ServiceLogo, categoryColor } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const nav = [
  { to: "/admin", label: "Dashboard", key: "dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users / यूज़र", key: "users", icon: Users },
  { to: "/admin/categories", label: "Categories", key: "categories", icon: FolderTree },
  { to: "/admin/services", label: "Services", key: "services", icon: Package },
  { to: "/admin/leads", label: "Leads", key: "leads", icon: ClipboardList },
  { to: "/admin/payment-qrs", label: "Payment QRs", key: "payment-qrs", icon: QrCode },
  { to: "/admin/payments", label: "Payment Approvals", key: "payments", icon: IndianRupee },
  { to: "/admin/kyc", label: "Merchant KYC", key: "kyc", icon: BadgeCheck },
  { to: "/admin/wallet", label: "Wallet Ops", key: "wallet", icon: Wallet },
  { to: "/admin/targets", label: "Targets", key: "targets", icon: Target },
  { to: "/admin/notifications", label: "Notifications", key: "notifications", icon: Bell },
  { to: "/admin/commission", label: "Commission Sheet", key: "commission", icon: FileText },
  { to: "/admin/support", label: "Support Info", key: "support", icon: HeadphonesIcon },
  { to: "/admin/reports", label: "Reports", key: "reports", icon: FileText },
];

function Home() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/reports/summary").then((r) => setS(r.data)); }, []);
  return (
    <DashboardLayout roleLabel="Admin Panel" nav={nav} title="Dashboard / डैशबोर्ड" subtitle="Complete overview of EPAYCASH platform">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-total-leads" label="Total Leads" value={s?.total_leads ?? "—"} icon={ClipboardList} accent="blue" />
        <KpiCard testId="kpi-approved" label="Approved" value={s?.approved ?? "—"} icon={CheckCircle2} accent="green" />
        <KpiCard testId="kpi-pending" label="Pending" value={s?.pending ?? "—"} icon={AlertCircle} accent="orange" />
        <KpiCard testId="kpi-rejected" label="Rejected" value={s?.rejected ?? "—"} icon={XCircle} accent="red" />
        <KpiCard testId="kpi-etb" label="ETB" value={s?.etb ?? "—"} icon={TrendingUp} accent="green" />
        <KpiCard testId="kpi-ntb" label="NTB" value={s?.ntb ?? "—"} icon={UserCircle2} accent="sky" />
        <KpiCard testId="kpi-duplicate" label="Duplicate" value={s?.duplicate ?? "—"} icon={Copy} accent="red" />
        <KpiCard testId="kpi-merchants" label="Total Merchants" value={s?.total_merchants ?? "—"} icon={Building2} accent="violet" />
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <KpiCard testId="kpi-kyc-pending" label="KYC Pending" value={s?.kyc_pending ?? "—"} icon={BadgeCheck} accent="orange" hint="Merchant KYC awaiting review" />
        <div className="bg-white border rounded-md p-6">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">Getting Started</div>
          <ul className="mt-3 space-y-2 text-sm">
            <li>1. Categories और Services add करें</li>
            <li>2. Vendor / Employee accounts create करें</li>
            <li>3. Leads receive होना शुरू करें</li>
            <li>4. Merchant KYC approve करें और wallet manage करें</li>
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
}

function UsersPage() {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ mobile: "", password: "", name: "", email: "", role: "vendor", business_name: "" });
  const [vendors, setVendors] = useState([]);
  const [shown, setShown] = useState({});
  const [resetting, setResetting] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [profileUser, setProfileUser] = useState(null);

  const load = async () => {
    const params = filter === "all" ? { include_password: true } : { role: filter, include_password: true };
    const { data } = await api.get("/users", { params });
    setUsers(data);
    const v = await api.get("/users", { params: { role: "vendor" } });
    setVendors(v.data);
  };
  useEffect(() => { load(); }, [filter]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ mobile: "", password: "", name: "", email: "", role: "vendor", business_name: "" });
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const toggle = async (id) => {
    try { await api.patch(`/users/${id}/toggle`); load(); } catch (e) { toast.error(formatError(e)); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/users/${resetting.id}/reset-password`, { new_password: newPwd });
      toast.success(`Password reset for ${resetting.name}`);
      setResetting(null); setNewPwd(""); load();
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <DashboardLayout roleLabel="Admin Panel" nav={nav} title="Users / यूज़र" subtitle="Vendors, Employees, Merchants manage करें">
      <div className="flex items-center justify-between mb-4">
        <div className="w-48">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="users-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="vendor">Vendors</SelectItem>
              <SelectItem value="employee">Employees</SelectItem>
              <SelectItem value="merchant">Merchants</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand-gradient hover:opacity-90 text-white" data-testid="add-user-btn">+ Add User</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, parent_id: undefined })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vendor">Vendor</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="merchant">Merchant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
              <div><Label>Mobile</Label><Input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="new-user-mobile" /></div>
              <div><Label>Password</Label><Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
              {(form.role === "employee" || form.role === "merchant") && (
                <div className="col-span-2"><Label>Attach to Vendor (optional)</Label>
                  <Select value={form.parent_id || "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="new-user-parent"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} ({v.mobile})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2"><Label>Business Name (optional)</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="new-user-business" /></div>
              <DialogFooter className="col-span-2"><Button type="submit" data-testid="submit-new-user">Create</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Mobile</TableHead><TableHead>Password</TableHead><TableHead>Role</TableHead><TableHead>Wallet</TableHead><TableHead>KYC</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="font-mono text-sm">{u.mobile}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs" data-testid={`pwd-cell-${u.id}`}>
                      {shown[u.id] ? (u.password_plain || "—") : "••••••••"}
                    </span>
                    {u.password_plain && (
                      <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => setShown((s) => ({ ...s, [u.id]: !s[u.id] }))} data-testid={`pwd-toggle-${u.id}`}>
                        {shown[u.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                  </div>
                </TableCell>
                <TableCell><span className="text-xs uppercase tracking-widest">{u.role}</span></TableCell>
                <TableCell>₹ {(u.wallet_balance || 0).toFixed(2)}</TableCell>
                <TableCell>{u.kyc_status ? <StatusBadge status={u.kyc_status} /> : "—"}</TableCell>
                <TableCell>{u.active ? <StatusBadge status="approved" /> : <StatusBadge status="rejected" />}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button variant="outline" size="sm" onClick={() => setProfileUser(u)} data-testid={`view-profile-${u.id}`}><UserIcon className="h-3.5 w-3.5 mr-1" /> View Full Profile</Button>
                  <Button variant="outline" size="sm" onClick={() => { setResetting(u); setNewPwd(""); }} data-testid={`reset-pwd-${u.id}`}><KeyRound className="h-3.5 w-3.5 mr-1" /> Reset</Button>
                  <Button variant="outline" size="sm" onClick={() => toggle(u.id)}>{u.active ? "Disable" : "Enable"}</Button>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">No users found</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!resetting} onOpenChange={(o) => { if (!o) setResetting(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reset Password — {resetting?.name}</DialogTitle></DialogHeader>
          <form onSubmit={submitReset} className="space-y-3">
            <div className="text-xs text-slate-500">Mobile: <span className="font-mono">{resetting?.mobile}</span></div>
            <div><Label>New Password</Label><Input required type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="reset-new-pwd" /></div>
            <DialogFooter><Button type="submit" className="bg-brand-gradient text-white" data-testid="reset-submit">Reset Password</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <FullProfileDialog user={profileUser} onClose={() => setProfileUser(null)} />
    </DashboardLayout>
  );
}

function CategoriesPage() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/categories", { name, description: desc }); setName(""); setDesc(""); load(); toast.success("Category added"); }
    catch (err) { toast.error(formatError(err)); }
  };
  const remove = async (id) => { await api.delete(`/categories/${id}`); load(); };
  return (
    <DashboardLayout roleLabel="Admin Panel" nav={nav} title="Categories / श्रेणियाँ" subtitle="Service categories manage करें">
      <form onSubmit={add} className="bg-white border rounded-md p-5 flex flex-wrap gap-3 items-end mb-4">
        <div className="flex-1 min-w-[200px]"><Label>Name</Label><Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="cat-name" /></div>
        <div className="flex-1 min-w-[200px]"><Label>Description</Label><Input value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="cat-desc" /></div>
        <Button type="submit" className="bg-[#1a3ba8] hover:bg-[#17348f]" data-testid="cat-add-btn">Add Category</Button>
      </form>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {cats.map((c) => (
          <div key={c.id} className="stat-card bg-white border rounded-md p-5" data-testid={`cat-${c.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-display font-semibold text-lg">{c.name}</div>
                <div className="text-sm text-slate-500 mt-1">{c.description || "—"}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(c.id)} className="text-rose-600 hover:text-rose-700">Delete</Button>
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

function ServicesPage() {
  const [items, setItems] = useState([]); const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ name: "", category_id: "", service_type: "lead_form", open_link: "", description: "", payout: 0, logo_url: "", charge_type: "free", charge_amount: 0, merchant_commission: 0, vendor_commission: 0, active: true });
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editUploading, setEditUploading] = useState(false);
  const fileRef = React.useRef(null);
  const editFileRef = React.useRef(null);
  const load = async () => { setItems((await api.get("/services")).data); setCats((await api.get("/categories")).data); };
  useEffect(() => { load(); }, []);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only image files"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Max 2 MB"); return; }
    setUploading(true);
    try {
      const absUrl = await uploadFile(file);
      setForm((f) => ({ ...f, logo_url: absUrl }));
      toast.success("Logo uploaded");
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const handleEditFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only image files"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Max 2 MB"); return; }
    setEditUploading(true);
    try {
      const absUrl = await uploadFile(file);
      setEditing((s) => ({ ...s, logo_url: absUrl }));
      toast.success("Logo updated");
    } catch (err) { toast.error(formatError(err)); }
    finally { setEditUploading(false); if (editFileRef.current) editFileRef.current.value = ""; }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.category_id) return toast.error("Category select करें");
    try { await api.post("/services", { ...form, payout: Number(form.payout), charge_amount: Number(form.charge_amount), merchant_commission: Number(form.merchant_commission), vendor_commission: Number(form.vendor_commission) }); setForm({ name: "", category_id: "", service_type: "lead_form", open_link: "", description: "", payout: 0, logo_url: "", charge_type: "free", charge_amount: 0, merchant_commission: 0, vendor_commission: 0, active: true }); load(); toast.success("Service added"); }
    catch (err) { toast.error(formatError(err)); }
  };
  const remove = async (id) => { await api.delete(`/services/${id}`); load(); };
  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: editing.name,
        category_id: editing.category_id,
        service_type: editing.service_type,
        open_link: editing.open_link || "",
        description: editing.description || "",
        payout: Number(editing.payout || 0),
        logo_url: editing.logo_url || "",
      };
      await api.patch(`/services/${editing.id}`, payload);
      toast.success("Service updated");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <DashboardLayout roleLabel="Admin Panel" nav={nav} title="Services / सेवाएँ" subtitle="Services add / edit करें, logo upload करें और type set करें">
      <form onSubmit={add} className="bg-white border rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="md:col-span-2 flex items-center gap-4">
          <ServiceLogo url={form.logo_url} name={form.name || "Service"} size={72} />
          <div className="flex-1">
            <Label>Service Logo / लोगो (Image Upload)</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="svc-logo-file" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="svc-logo-upload-btn">
                {uploading ? "Uploading…" : form.logo_url ? "Change Logo" : "Upload Logo"}
              </Button>
              {form.logo_url && (
                <Button type="button" variant="ghost" onClick={() => setForm({ ...form, logo_url: "" })} className="text-rose-600" data-testid="svc-logo-clear">Remove</Button>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">PNG / JPG / WebP / SVG, max 2 MB.</p>
          </div>
        </div>
        <div><Label>Service Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="svc-name" /></div>
        <div><Label>Category</Label>
          <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
            <SelectTrigger data-testid="svc-cat"><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Type</Label>
          <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
            <SelectTrigger data-testid="svc-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open_link">Open Link</SelectItem>
              <SelectItem value="lead_form">Lead Form</SelectItem>
              <SelectItem value="link_and_form">Link + Lead Form</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Payout (₹)</Label><Input type="number" value={form.payout} onChange={(e) => setForm({ ...form, payout: e.target.value })} data-testid="svc-payout" /></div>
        <div><Label>Charge Type / प्रकार</Label>
          <Select value={form.charge_type} onValueChange={(v) => setForm({ ...form, charge_type: v })}>
            <SelectTrigger data-testid="svc-charge-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="free">Free / मुफ्त</SelectItem>
              <SelectItem value="paid">Paid / Charges लगेंगे</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.charge_type === "paid" && (
          <div><Label>Charge Amount (₹) / चार्ज राशि</Label><Input type="number" value={form.charge_amount} onChange={(e) => setForm({ ...form, charge_amount: e.target.value })} data-testid="svc-charge-amt" /></div>
        )}
        <div><Label>Merchant Commission (₹)</Label><Input type="number" value={form.merchant_commission} onChange={(e) => setForm({ ...form, merchant_commission: e.target.value })} data-testid="svc-mcomm" /></div>
        <div><Label>Vendor Commission (₹)</Label><Input type="number" value={form.vendor_commission} onChange={(e) => setForm({ ...form, vendor_commission: e.target.value })} data-testid="svc-vcomm" /></div>
        <div className="md:col-span-2"><Label>Open Link (optional)</Label><Input value={form.open_link} onChange={(e) => setForm({ ...form, open_link: e.target.value })} data-testid="svc-link" placeholder="https://..." /></div>
        <div className="md:col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="svc-desc" /></div>
        <div className="md:col-span-2 flex justify-end"><Button type="submit" className="bg-brand-gradient hover:opacity-90 text-white rounded-full px-6" data-testid="svc-add">Add Service</Button></div>
      </form>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Logo</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Type</TableHead><TableHead>Payout</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((s) => {
              const cat = cats.find((c) => c.id === s.category_id);
              const cc = categoryColor(cat?.name);
              return (
                <TableRow key={s.id} data-testid={`svc-row-${s.id}`}>
                  <TableCell><ServiceLogo url={s.logo_url} name={s.name} size={36} /></TableCell>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell><span className={`chip border ${cc.chip}`}>{cat?.name || "—"}</span></TableCell>
                  <TableCell><span className="inline-flex px-2 py-0.5 rounded-md border text-[11px] font-medium tracking-wide bg-[#1a3ba8]/5 text-[#1a3ba8] border-[#1a3ba8]/20">{s.service_type}</span></TableCell>
                  <TableCell>₹ {Number(s.payout || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="outline" size="sm" onClick={() => setEditing({ ...s })} data-testid={`svc-edit-${s.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(s.id)} className="text-rose-600" data-testid={`svc-delete-${s.id}`}>Delete</Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No services</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Edit Service</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 flex items-center gap-4">
                <ServiceLogo url={editing.logo_url} name={editing.name} size={64} />
                <div>
                  <input ref={editFileRef} type="file" accept="image/*" onChange={handleEditFile} className="hidden" data-testid="svc-edit-logo-file" />
                  <Button type="button" variant="outline" onClick={() => editFileRef.current?.click()} disabled={editUploading} data-testid="svc-edit-logo-btn">
                    {editUploading ? "Uploading…" : "Change Logo"}
                  </Button>
                </div>
              </div>
              <div><Label>Name</Label><Input required value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="svc-edit-name" /></div>
              <div><Label>Category</Label>
                <Select value={editing.category_id} onValueChange={(v) => setEditing({ ...editing, category_id: v })}>
                  <SelectTrigger data-testid="svc-edit-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Type</Label>
                <Select value={editing.service_type} onValueChange={(v) => setEditing({ ...editing, service_type: v })}>
                  <SelectTrigger data-testid="svc-edit-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open_link">Open Link</SelectItem>
                    <SelectItem value="lead_form">Lead Form</SelectItem>
                    <SelectItem value="link_and_form">Link + Lead Form</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Payout (₹)</Label><Input type="number" value={editing.payout || 0} onChange={(e) => setEditing({ ...editing, payout: e.target.value })} data-testid="svc-edit-payout" /></div>
              <div className="md:col-span-2"><Label>Open Link</Label><Input value={editing.open_link || ""} onChange={(e) => setEditing({ ...editing, open_link: e.target.value })} data-testid="svc-edit-link" /></div>
              <div className="md:col-span-2"><Label>Description</Label><Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="svc-edit-desc" /></div>
              <DialogFooter className="md:col-span-2">
                <Button type="submit" className="bg-brand-gradient text-white" data-testid="svc-edit-save">Save Changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [filters, setFilters] = useState({ role: "all", vendor: "all", service: "all", status: "all", search: "" });
  const load = async () => {
    setLeads((await api.get("/leads")).data);
    setServices((await api.get("/services")).data);
    setUsers((await api.get("/users")).data);
  };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { try { await api.patch(`/leads/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); } catch (e) { toast.error(formatError(e)); } };

  const vendorList = users.filter((u) => u.role === "vendor");
  const filtered = leads.filter((l) => {
    if (filters.role !== "all" && (l.creator_role || "") !== filters.role) return false;
    if (filters.vendor !== "all" && (l.parent_vendor_id || "") !== filters.vendor) return false;
    if (filters.service !== "all" && l.service_id !== filters.service) return false;
    if (filters.status !== "all" && l.status !== filters.status) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const hay = `${l.customer_name || ""} ${l.customer_mobile || ""} ${l.created_by_name || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <DashboardLayout roleLabel="Admin Panel" nav={nav} title="Leads / लीड्स" subtitle="Approve, Reject, ETB, NTB, Duplicate — Created By tracking के साथ">
      <div className="bg-white border rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
        <div><Label className="text-xs">Created By Role</Label>
          <Select value={filters.role} onValueChange={(v) => setFilters({ ...filters, role: v })}>
            <SelectTrigger data-testid="lead-filter-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="vendor">Vendor</SelectItem>
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="merchant">Merchant</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Parent Vendor</Label>
          <Select value={filters.vendor} onValueChange={(v) => setFilters({ ...filters, vendor: v })}>
            <SelectTrigger data-testid="lead-filter-vendor"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vendors</SelectItem>
              {vendorList.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Service</Label>
          <Select value={filters.service} onValueChange={(v) => setFilters({ ...filters, service: v })}>
            <SelectTrigger data-testid="lead-filter-service"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Services</SelectItem>
              {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="lead-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {["pending","approved","rejected","etb","ntb","duplicate"].map((s) => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2"><Label className="text-xs">Search (Name / Mobile / Creator)</Label>
          <Input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="Type to filter..." data-testid="lead-filter-search" />
        </div>
      </div>

      <div className="text-xs text-slate-500 mb-2">Showing {filtered.length} of {leads.length} leads</div>

      <div className="bg-white border rounded-xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Mobile</TableHead><TableHead>Service</TableHead><TableHead>Created By</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((l) => {
              const svc = services.find((s) => s.id === l.service_id);
              const isLegacy = !l.created_by_name && !l.creator_role;
              return (
                <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                  <TableCell className="font-medium">
                    <button className="hover:underline text-left" onClick={() => setViewing(l)} data-testid={`lead-view-${l.id}`}>{l.customer_name}</button>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{l.customer_mobile}</TableCell>
                  <TableCell>{svc?.name || l.service_id}</TableCell>
                  <TableCell className="text-sm">
                    {isLegacy
                      ? <span className="chip border bg-slate-100 text-slate-500 border-slate-200 text-[10px]">LEGACY / Source N/A</span>
                      : (l.created_by_name || <span className="text-slate-400">—</span>)}
                  </TableCell>
                  <TableCell>
                    {l.creator_role
                      ? <span className="inline-flex px-2 py-0.5 rounded-md border text-[10px] font-medium uppercase tracking-wide bg-slate-50 text-slate-700 border-slate-200">{l.creator_role}</span>
                      : <span className="text-slate-400 text-xs">—</span>}
                  </TableCell>
                  <TableCell><StatusBadge status={l.status} /></TableCell>
                  <TableCell className="text-xs text-slate-500">{(l.created_at || "").split("T")[0]}</TableCell>
                  <TableCell className="text-right">
                    <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                      <SelectTrigger className="w-36 ml-auto" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["pending","approved","rejected","etb","ntb","duplicate"].map((s) => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">No leads match filters</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Lead Details</DialogTitle></DialogHeader>
          {viewing && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ["Name", viewing.customer_name],
                ["Mobile", viewing.customer_mobile],
                ["Email", viewing.customer_email],
                ["Aadhaar", viewing.aadhaar_no],
                ["PAN", viewing.pan_no],
                ["State", viewing.state],
                ["District", viewing.district],
                ["Pin Code", viewing.pincode],
                ["Created By", viewing.created_by_name || (viewing.creator_role ? "—" : "LEGACY / Source N/A")],
                ["Creator Role", viewing.creator_role],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div>
                  <div className="mt-1 font-medium">{v || "—"}</div>
                </div>
              ))}
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Address</div>
                <div className="mt-1 font-medium whitespace-pre-wrap">{viewing.address || "—"}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Notes</div>
                <div className="mt-1 font-medium whitespace-pre-wrap">{viewing.notes || "—"}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Status</div>
                <div className="mt-1"><StatusBadge status={viewing.status} /></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function KycPage() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const load = async () => { setRows((await api.get("/kyc")).data); setUsers((await api.get("/users", { params: { role: "merchant" } })).data); };
  useEffect(() => { load(); }, []);
  const decide = async (uid, status) => { try { await api.patch(`/kyc/${uid}/decide`, { status }); toast.success(`KYC ${status}`); load(); } catch (e) { toast.error(formatError(e)); } };
  return (
    <DashboardLayout roleLabel="Admin Panel" nav={nav} title="Merchant KYC" subtitle="Merchant KYC submissions review करें">
      <div className="bg-white border rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Merchant</TableHead><TableHead>PAN</TableHead><TableHead>Aadhaar</TableHead><TableHead>Business</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((k) => {
              const u = users.find((x) => x.id === k.user_id);
              return (
                <TableRow key={k.user_id}>
                  <TableCell className="font-medium">{u?.name || k.user_id}<div className="text-xs text-slate-500 font-mono">{u?.mobile}</div></TableCell>
                  <TableCell>{k.pan || "—"}</TableCell>
                  <TableCell>{k.aadhaar || "—"}</TableCell>
                  <TableCell>{k.business_type || "—"}</TableCell>
                  <TableCell><StatusBadge status={k.status} /></TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => decide(k.user_id, "approved")} data-testid={`kyc-approve-${k.user_id}`}>Approve</Button>
                    <Button size="sm" variant="outline" className="text-rose-600" onClick={() => decide(k.user_id, "rejected")} data-testid={`kyc-reject-${k.user_id}`}>Reject</Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No KYC submissions</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </DashboardLayout>
  );
}

function WalletOpsPage() {
  const [users, setUsers] = useState([]);
  const [selUser, setSelUser] = useState("");
  const [amount, setAmount] = useState(""); const [type, setType] = useState("credit"); const [note, setNote] = useState("");
  const [txs, setTxs] = useState([]);
  const load = async () => { setUsers((await api.get("/users")).data); };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (selUser) api.get("/wallet/tx", { params: { user_id: selUser } }).then((r) => setTxs(r.data)); }, [selUser]);
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/wallet/tx", { user_id: selUser, amount: Number(amount), type, note }); toast.success(`Balance: ₹${data.balance}`); setAmount(""); setNote(""); load(); const r = await api.get("/wallet/tx", { params: { user_id: selUser } }); setTxs(r.data); } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <DashboardLayout roleLabel="Admin Panel" nav={nav} title="Wallet Operations" subtitle="Credit / Debit / Lock / Unlock user wallets">
      <form onSubmit={submit} className="bg-white border rounded-md p-5 grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
        <div className="md:col-span-2"><Label>User</Label>
          <Select value={selUser} onValueChange={setSelUser}>
            <SelectTrigger data-testid="wallet-user"><SelectValue placeholder="Select user" /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.mobile} ({u.role})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="wallet-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="credit">Credit</SelectItem><SelectItem value="debit">Debit</SelectItem>
              <SelectItem value="lock">Lock</SelectItem><SelectItem value="unlock">Unlock</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Amount</Label><Input type="number" required value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="wallet-amt" /></div>
        <div><Label>Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="wallet-note" /></div>
        <div className="md:col-span-5 flex justify-end"><Button type="submit" disabled={!selUser} className="bg-[#1a3ba8] hover:bg-[#17348f]" data-testid="wallet-submit">Apply</Button></div>
      </form>
      <div className="bg-white border rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Note</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
          <TableBody>
            {txs.map((t) => (
              <TableRow key={t.id}>
                <TableCell className="uppercase text-xs tracking-wide">{t.type}</TableCell>
                <TableCell>₹ {Number(t.amount).toFixed(2)}</TableCell>
                <TableCell>{t.note || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{(t.created_at || "").replace("T"," ").slice(0,16)}</TableCell>
              </TableRow>
            ))}
            {(!selUser || txs.length === 0) && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">Select a user to see transactions</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </DashboardLayout>
  );
}

function TargetsPage() {
  const [emps, setEmps] = useState([]); const [targets, setTargets] = useState([]);
  const [form, setForm] = useState({ employee_id: "", month: new Date().toISOString().slice(0,7), target_leads: 0, target_amount: 0 });
  const load = async () => { setEmps((await api.get("/users", { params: { role: "employee" } })).data); setTargets((await api.get("/targets")).data); };
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); try { await api.post("/targets", { ...form, target_leads: Number(form.target_leads), target_amount: Number(form.target_amount) }); toast.success("Target assigned"); load(); } catch (err) { toast.error(formatError(err)); } };
  return (
    <DashboardLayout roleLabel="Admin Panel" nav={nav} title="Targets / लक्ष्य" subtitle="Employees को monthly targets assign करें">
      <form onSubmit={submit} className="bg-white border rounded-md p-5 grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div><Label>Employee</Label>
          <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
            <SelectTrigger data-testid="target-emp"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.mobile}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Month (YYYY-MM)</Label><Input value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} data-testid="target-month" /></div>
        <div><Label>Target Leads</Label><Input type="number" value={form.target_leads} onChange={(e) => setForm({ ...form, target_leads: e.target.value })} data-testid="target-leads" /></div>
        <div><Label>Target Amount (₹)</Label><Input type="number" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} data-testid="target-amt" /></div>
        <div className="md:col-span-4 flex justify-end"><Button type="submit" disabled={!form.employee_id} className="bg-[#1a3ba8] hover:bg-[#17348f]" data-testid="target-submit">Assign Target</Button></div>
      </form>
      <div className="bg-white border rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Month</TableHead><TableHead>Target Leads</TableHead><TableHead>Target Amount</TableHead></TableRow></TableHeader>
          <TableBody>
            {targets.map((t) => { const e = emps.find((x) => x.id === t.employee_id); return (
              <TableRow key={t.id}>
                <TableCell>{e?.name || t.employee_id}</TableCell>
                <TableCell>{t.month}</TableCell>
                <TableCell>{t.target_leads}</TableCell>
                <TableCell>₹ {Number(t.target_amount).toFixed(2)}</TableCell>
              </TableRow>
            ); })}
            {targets.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">No targets set</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </DashboardLayout>
  );
}

function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", message: "", image_url: "", target_role: "all", target_user_id: "", cta_label: "", cta_url: "", active: true });
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  const load = () => api.get("/notifications").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const handleFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Only image files");
    if (file.size > 2 * 1024 * 1024) return toast.error("Max 2 MB");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, target_user_id: form.target_user_id || null };
      await api.post("/notifications", payload);
      toast.success("Notification created");
      setForm({ title: "", message: "", image_url: "", target_role: "all", target_user_id: "", cta_label: "", cta_url: "", active: true });
      load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const toggle = async (id) => { await api.patch(`/notifications/${id}/toggle`); load(); };
  const del = async (id) => { await api.delete(`/notifications/${id}`); load(); };
  return (
    <DashboardLayout roleLabel="Admin Panel" nav={nav} title="Notifications / नोटिफिकेशन" subtitle="Popup notifications बनाएँ — किसको दिखाना है admin तय करेगा">
      <form onSubmit={submit} className="bg-white border rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="md:col-span-2 flex items-center gap-4">
          <div className="w-24 h-16 rounded-md overflow-hidden bg-slate-100 border flex items-center justify-center">
            {form.image_url ? <img src={form.image_url} alt="preview" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-400 uppercase tracking-widest">No image</span>}
          </div>
          <div>
            <Label>Popup Image (optional)</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="notif-image-file" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="notif-image-btn">
                {uploading ? "Uploading…" : form.image_url ? "Change Image" : "Upload Image"}
              </Button>
              {form.image_url && <Button type="button" variant="ghost" className="text-rose-600" onClick={() => setForm({ ...form, image_url: "" })} data-testid="notif-image-clear">Remove</Button>}
            </div>
          </div>
        </div>
        <div><Label>Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="notif-title" /></div>
        <div><Label>Target Role</Label>
          <Select value={form.target_role} onValueChange={(v) => setForm({ ...form, target_role: v })}>
            <SelectTrigger data-testid="notif-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="vendor">Vendor</SelectItem>
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="merchant">Merchant</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2"><Label>Message</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="notif-message" /></div>
        <div><Label>CTA Button Label (optional)</Label><Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} placeholder="e.g., Learn More" data-testid="notif-cta-label" /></div>
        <div><Label>CTA URL (optional)</Label><Input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} placeholder="https://..." data-testid="notif-cta-url" /></div>
        <div className="md:col-span-2"><Label>Specific User ID (optional — overrides role)</Label><Input value={form.target_user_id} onChange={(e) => setForm({ ...form, target_user_id: e.target.value })} placeholder="UUID of specific user" data-testid="notif-user-id" /></div>
        <div className="md:col-span-2 flex justify-end"><Button type="submit" className="bg-brand-gradient hover:opacity-90 text-white rounded-full px-6" data-testid="notif-submit">Publish Notification</Button></div>
      </form>

      <div className="bg-white border rounded-xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Image</TableHead><TableHead>Title</TableHead><TableHead>Target</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((n) => (
              <TableRow key={n.id} data-testid={`notif-row-${n.id}`}>
                <TableCell>{n.image_url ? <img src={n.image_url} alt="" className="w-12 h-8 object-cover rounded" /> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                <TableCell className="font-medium">{n.title}<div className="text-xs text-slate-500 line-clamp-1">{n.message}</div></TableCell>
                <TableCell><span className="chip border bg-slate-100 text-slate-700 border-slate-200">{n.target_user_id ? `user:${n.target_user_id.slice(0,8)}` : n.target_role}</span></TableCell>
                <TableCell><StatusBadge status={n.active ? "approved" : "not_submitted"} /></TableCell>
                <TableCell className="text-xs text-slate-500">{(n.created_at || "").split("T")[0]}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => toggle(n.id)} data-testid={`notif-toggle-${n.id}`}>{n.active ? "Disable" : "Enable"}</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(n.id)} className="text-rose-600" data-testid={`notif-del-${n.id}`}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No notifications yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </DashboardLayout>
  );
}

function CommissionSheetPage() {
  const [items, setItems] = useState([]); const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => { setItems((await api.get("/services")).data); setCats((await api.get("/categories")).data); };
  useEffect(() => { load(); }, []);
  const toggleActive = async (s) => { try { const willActivate = s.active === false; await api.patch(`/services/${s.id}`, { active: willActivate }); load(); toast.success(willActivate ? "Activated" : "Deactivated"); } catch (e) { toast.error(formatError(e)); } };
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/services/${editing.id}`, {
        name: editing.name,
        charge_type: editing.charge_type,
        charge_amount: Number(editing.charge_amount || 0),
        merchant_commission: Number(editing.merchant_commission || 0),
        vendor_commission: Number(editing.vendor_commission || 0),
        active: editing.active,
      });
      toast.success("Commission updated");
      setEditing(null); load();
    } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <DashboardLayout roleLabel="Admin Panel" nav={nav} title="Commission Sheet / कमीशन शीट" subtitle="हर service की Charges / Free status और Merchant + Vendor commission set करें">
      <div className="bg-white border rounded-xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>SR</TableHead><TableHead>Service Name</TableHead><TableHead>Service Type</TableHead>
            <TableHead>Type</TableHead><TableHead>Amount</TableHead>
            <TableHead>Merchant ₹</TableHead><TableHead>Vendor ₹</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((s, i) => {
              const cat = cats.find((c) => c.id === s.category_id);
              return (
                <TableRow key={s.id} data-testid={`comm-row-${s.id}`}>
                  <TableCell className="font-mono text-xs">{i + 1}</TableCell>
                  <TableCell className="font-medium">{s.name}<div className="text-xs text-slate-500">{cat?.name || "—"}</div></TableCell>
                  <TableCell><span className="chip border bg-[#1a3ba8]/5 text-[#1a3ba8] border-[#1a3ba8]/20">{s.service_type}</span></TableCell>
                  <TableCell>
                    {s.charge_type === "paid"
                      ? <span className="chip border bg-orange-50 text-orange-700 border-orange-200">CHARGES</span>
                      : <span className="chip border bg-emerald-50 text-emerald-700 border-emerald-200">FREE</span>}
                  </TableCell>
                  <TableCell>{s.charge_type === "paid" ? `₹ ${Number(s.charge_amount || 0).toFixed(2)}` : "—"}</TableCell>
                  <TableCell>₹ {Number(s.merchant_commission || 0).toFixed(2)}</TableCell>
                  <TableCell>₹ {Number(s.vendor_commission || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    {s.active !== false
                      ? <span className="chip border bg-emerald-50 text-emerald-700 border-emerald-200">ACTIVE</span>
                      : <span className="chip border bg-slate-100 text-slate-600 border-slate-200">INACTIVE</span>}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...s })} data-testid={`comm-edit-${s.id}`}>Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(s)} data-testid={`comm-toggle-${s.id}`}>{s.active !== false ? "Deactivate" : "Activate"}</Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">No services. Services page पर जाकर service add करें.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit Commission — {editing?.name}</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={save} className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={editing.charge_type || "free"} onValueChange={(v) => setEditing({ ...editing, charge_type: v })}>
                  <SelectTrigger data-testid="comm-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="paid">Charges (Paid)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editing.charge_type === "paid" && (
                <div><Label>Amount (₹)</Label><Input type="number" value={editing.charge_amount || 0} onChange={(e) => setEditing({ ...editing, charge_amount: e.target.value })} data-testid="comm-amount" /></div>
              )}
              <div><Label>Merchant Commission (₹)</Label><Input type="number" value={editing.merchant_commission || 0} onChange={(e) => setEditing({ ...editing, merchant_commission: e.target.value })} data-testid="comm-mcomm" /></div>
              <div><Label>Vendor Commission (₹)</Label><Input type="number" value={editing.vendor_commission || 0} onChange={(e) => setEditing({ ...editing, vendor_commission: e.target.value })} data-testid="comm-vcomm" /></div>
              <DialogFooter className="col-span-2"><Button type="submit" className="bg-brand-gradient text-white" data-testid="comm-save">Save</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function SupportSettingsPage() {
  const [form, setForm] = useState({ customer_care: "", whatsapp: "", email: "", office_address: "" });
  const load = () => api.get("/settings/support").then((r) => setForm(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.patch("/settings/support", form); toast.success("Support info updated"); } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <DashboardLayout roleLabel="Admin Panel" nav={nav} title="Support Us / सहायता जानकारी" subtitle="Customer care, WhatsApp, email और office address — merchants को दिखने वाली जानकारी">
      <form onSubmit={submit} className="bg-white border rounded-xl p-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
        <div><Label>Customer Care Number</Label><Input value={form.customer_care || ""} onChange={(e) => setForm({ ...form, customer_care: e.target.value })} data-testid="sup-care" /></div>
        <div><Label>WhatsApp Number</Label><Input value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="sup-whatsapp" /></div>
        <div className="md:col-span-2"><Label>Email ID</Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sup-email" /></div>
        <div className="md:col-span-2"><Label>Office Address</Label><Textarea value={form.office_address || ""} onChange={(e) => setForm({ ...form, office_address: e.target.value })} data-testid="sup-address" /></div>
        <div className="md:col-span-2 flex justify-end"><Button type="submit" className="bg-brand-gradient text-white rounded-full px-6" data-testid="sup-save">Save Changes</Button></div>
      </form>
    </DashboardLayout>
  );
}

function ReportsPage() {
  const dl = (kind, fmt) => {
    const token = localStorage.getItem("epc_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/${fmt}?kind=${kind}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `${kind}.${fmt === "xlsx" ? "xlsx" : fmt === "pdf" ? "pdf" : "csv"}`; a.click(); });
  };
  const reports = [
    { kind: "leads", name: "All Leads", desc: "पूरी platform के leads with status और customer info.", grad: "bg-brand-gradient" },
    { kind: "merchants", name: "Merchants", desc: "Merchant list with KYC & wallet balance.", grad: "bg-emerald-gradient" },
    { kind: "employees", name: "Employees", desc: "Employees की complete list और उनका vendor mapping.", grad: "bg-violet-gradient" },
    { kind: "vendors", name: "Vendors", desc: "Registered vendors की list.", grad: "bg-sky-gradient" },
    { kind: "wallet", name: "Wallet Transactions", desc: "Credit / Debit / Lock / Unlock history.", grad: "bg-orange-gradient" },
    { kind: "withdrawals", name: "Withdrawals", desc: "Merchant withdrawal requests और decisions.", grad: "bg-rose-gradient" },
  ];
  return (
    <DashboardLayout roleLabel="Admin Panel" nav={nav} title="Reports / रिपोर्ट्स" subtitle="Excel और PDF दोनों में download कर सकते हैं">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reports.map((r) => (
          <div key={r.kind} className="stat-card bg-white border rounded-xl p-6 relative overflow-hidden">
            <div className={`absolute -top-10 -right-10 w-28 h-28 rounded-full ${r.grad} opacity-10`} />
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">{r.name}</div>
            <h3 className="font-display text-xl font-semibold mt-2">{r.name} Report</h3>
            <p className="text-sm text-slate-500 mt-2">{r.desc}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" className="bg-emerald-gradient hover:opacity-90 text-white" onClick={() => dl(r.kind, "xlsx")} data-testid={`dl-xlsx-${r.kind}`}>Excel</Button>
              <Button size="sm" className="bg-rose-gradient hover:opacity-90 text-white" onClick={() => dl(r.kind, "pdf")} data-testid={`dl-pdf-${r.kind}`}>PDF</Button>
              {(r.kind === "leads" || r.kind === "merchants") && (
                <Button size="sm" variant="outline" onClick={() => dl(r.kind, "csv")} data-testid={`dl-csv-${r.kind}`}>CSV</Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

function PaymentQRsPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", upi_id: "", qr_image_url: "", active: true });
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editUploading, setEditUploading] = useState(false);
  const fileRef = React.useRef(null);
  const editFileRef = React.useRef(null);
  const load = () => api.get("/payment-qrs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Only image files");
    if (file.size > 2 * 1024 * 1024) return toast.error("Max 2 MB");
    setUploading(true);
    try { const url = await upload(file); setForm((f) => ({ ...f, qr_image_url: url })); toast.success("QR uploaded"); }
    catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const handleEditFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setEditUploading(true);
    try { const url = await upload(file); setEditing((s) => ({ ...s, qr_image_url: url })); toast.success("QR updated"); }
    catch (err) { toast.error(formatError(err)); }
    finally { setEditUploading(false); if (editFileRef.current) editFileRef.current.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.qr_image_url) return toast.error("QR image upload करें");
    try {
      await api.post("/payment-qrs", form);
      toast.success("Payment QR added");
      setForm({ name: "", upi_id: "", qr_image_url: "", active: true }); load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    try { await api.patch(`/payment-qrs/${editing.id}`, editing); toast.success("QR updated"); setEditing(null); load(); }
    catch (err) { toast.error(formatError(err)); }
  };
  const toggle = async (q) => { try { await api.patch(`/payment-qrs/${q.id}`, { active: !q.active }); load(); } catch (e) { toast.error(formatError(e)); } };
  const remove = async (id) => { if (!window.confirm("Delete this QR?")) return; await api.delete(`/payment-qrs/${id}`); load(); };

  return (
    <DashboardLayout roleLabel="Admin Panel" nav={nav} title="Payment QRs" subtitle="UPI QR codes जो portal में Merchant/Vendor/Employee को दिखाई देंगे">
      <form onSubmit={submit} className="bg-white border rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="md:col-span-2 flex items-center gap-4">
          <div className="w-24 h-24 rounded-lg overflow-hidden bg-slate-100 border flex items-center justify-center">
            {form.qr_image_url
              ? <img src={form.qr_image_url} alt="QR" className="w-full h-full object-contain" />
              : <QrCode className="h-10 w-10 text-slate-300" />}
          </div>
          <div>
            <Label>QR Image Upload *</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="qr-file" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="qr-upload-btn">
                {uploading ? "Uploading…" : form.qr_image_url ? "Change QR" : "Upload QR"}
              </Button>
              {form.qr_image_url && <Button type="button" variant="ghost" className="text-rose-600" onClick={() => setForm({ ...form, qr_image_url: "" })}>Remove</Button>}
            </div>
            <p className="text-xs text-slate-500 mt-1">PNG/JPG, max 2 MB.</p>
          </div>
        </div>
        <div><Label>QR Name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., EPAYCASH Main UPI" data-testid="qr-name" /></div>
        <div><Label>UPI ID *</Label><Input required value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} placeholder="epaycash@ybl" data-testid="qr-upi" /></div>
        <div className="md:col-span-2 flex justify-end">
          <Button type="submit" className="bg-brand-gradient text-white rounded-full px-6" data-testid="qr-submit">+ Add Payment QR</Button>
        </div>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((q) => (
          <div key={q.id} className="bg-white border rounded-xl p-4 relative overflow-hidden" data-testid={`qr-card-${q.id}`}>
            <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full bg-brand-gradient opacity-10" />
            <div className="flex items-start gap-3">
              <img src={q.qr_image_url} alt={q.name} className="w-24 h-24 object-contain bg-slate-50 rounded-lg border" />
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold">{q.name}</div>
                <div className="text-xs text-slate-500 font-mono break-all">{q.upi_id}</div>
                <div className="mt-2">
                  {q.active
                    ? <span className="chip border bg-emerald-50 text-emerald-700 border-emerald-200">ACTIVE</span>
                    : <span className="chip border bg-slate-100 text-slate-600 border-slate-200">INACTIVE</span>}
                </div>
              </div>
            </div>
            <div className="mt-3 flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => setEditing({ ...q })} data-testid={`qr-edit-${q.id}`}>Edit</Button>
              <Button size="sm" variant="outline" onClick={() => toggle(q)} data-testid={`qr-toggle-${q.id}`}>{q.active ? "Deactivate" : "Activate"}</Button>
              <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(q.id)} data-testid={`qr-del-${q.id}`}>Delete</Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center text-slate-500 py-8 bg-white border rounded-xl">No Payment QRs yet</div>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit Payment QR</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-3">
              <div className="flex items-center gap-3">
                <img src={editing.qr_image_url} alt="" className="w-20 h-20 object-contain bg-slate-50 rounded border" />
                <input ref={editFileRef} type="file" accept="image/*" onChange={handleEditFile} className="hidden" />
                <Button type="button" variant="outline" onClick={() => editFileRef.current?.click()} disabled={editUploading}>
                  {editUploading ? "Uploading…" : "Change QR"}
                </Button>
              </div>
              <div><Label>Name</Label><Input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><Label>UPI ID</Label><Input required value={editing.upi_id} onChange={(e) => setEditing({ ...editing, upi_id: e.target.value })} /></div>
              <DialogFooter><Button type="submit" className="bg-brand-gradient text-white">Save</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function PaymentApprovalsPage() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [viewing, setViewing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => {
    const params = filter === "all" ? {} : { status_f: filter };
    api.get("/payment-requests", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [filter]);
  const approve = async (id) => { try { const { data } = await api.patch(`/payment-requests/${id}/approve`); toast.success(`Approved. New balance: ₹${data.new_balance.toFixed(2)}`); load(); } catch (e) { toast.error(formatError(e)); } };
  const submitReject = async (e) => {
    e.preventDefault();
    try { await api.patch(`/payment-requests/${rejecting.id}/reject`, { reason }); toast.success("Payment rejected"); setRejecting(null); setReason(""); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <DashboardLayout roleLabel="Admin Panel" nav={nav} title="Payment Approvals" subtitle="Users से आए payment submissions को verify और approve/reject करें">
      <div className="flex items-center justify-between mb-4">
        <div className="w-48">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="pay-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500">Total: <b>{rows.length}</b></div>
      </div>

      <div className="bg-white border rounded-xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>Service</TableHead>
            <TableHead>Amount</TableHead><TableHead>TXN ID</TableHead><TableHead>RRN</TableHead>
            <TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id} data-testid={`pay-row-${p.id}`}>
                <TableCell className="font-medium">{p.user_name}<div className="text-xs text-slate-500 font-mono">{p.user_mobile}</div></TableCell>
                <TableCell><span className="inline-flex px-2 py-0.5 rounded-md border text-[10px] font-medium uppercase tracking-wide bg-slate-50 text-slate-700 border-slate-200">{p.user_role}</span></TableCell>
                <TableCell className="text-sm">{p.service_name}</TableCell>
                <TableCell className="font-medium">₹ {Number(p.amount).toFixed(2)}</TableCell>
                <TableCell className="font-mono text-xs">{p.txn_id}</TableCell>
                <TableCell className="font-mono text-xs">{p.rrn_number}</TableCell>
                <TableCell className="text-xs text-slate-500">{p.payment_date}</TableCell>
                <TableCell><StatusBadge status={p.status} /></TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => setViewing(p)} data-testid={`pay-view-${p.id}`}>View</Button>
                  {p.status === "pending" && (
                    <>
                      <Button size="sm" className="bg-emerald-gradient text-white" onClick={() => approve(p.id)} data-testid={`pay-approve-${p.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="text-rose-600" onClick={() => { setRejecting(p); setReason(""); }} data-testid={`pay-reject-${p.id}`}>Reject</Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">No payment requests</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Payment Details</DialogTitle></DialogHeader>
          {viewing && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ["User", `${viewing.user_name} (${viewing.user_mobile})`],
                ["Role", viewing.user_role],
                ["Service", viewing.service_name],
                ["Amount", `₹ ${Number(viewing.amount).toFixed(2)}`],
                ["TXN ID", viewing.txn_id],
                ["RRN Number", viewing.rrn_number],
                ["Payment Date", viewing.payment_date],
                ["Status", viewing.status],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div>
                  <div className="mt-1 font-medium">{v || "—"}</div>
                </div>
              ))}
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Remark</div>
                <div className="mt-1">{viewing.remark || "—"}</div>
              </div>
              {viewing.reject_reason && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-rose-600">Reject Reason</div>
                  <div className="mt-1 text-rose-700">{viewing.reject_reason}</div>
                </div>
              )}
              {viewing.screenshot_url && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Payment Screenshot</div>
                  <a href={viewing.screenshot_url} target="_blank" rel="noreferrer">
                    <img src={viewing.screenshot_url} alt="screenshot" className="max-h-80 rounded-lg border" />
                  </a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejecting} onOpenChange={(o) => { if (!o) { setRejecting(null); setReason(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reject Payment</DialogTitle></DialogHeader>
          <form onSubmit={submitReject} className="space-y-3">
            <div className="text-xs text-slate-500">TXN: <span className="font-mono">{rejecting?.txn_id}</span> · Amount: ₹{Number(rejecting?.amount || 0).toFixed(2)}</div>
            <div><Label>Rejection Reason *</Label><Textarea required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Please explain why..." data-testid="pay-reject-reason" /></div>
            <DialogFooter><Button type="submit" variant="outline" className="text-rose-600" data-testid="pay-reject-submit">Confirm Reject</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function FullProfileDialog({ user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!user) { setData(null); return; }
    setLoading(true);
    api.get(`/users/${user.id}/full-profile`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setLoading(false));
  }, [user]);

  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Full Profile — {user?.name}</DialogTitle>
        </DialogHeader>
        {loading && <div className="text-center py-10 text-slate-500">Loading…</div>}
        {data && !loading && (
          <div>
            {/* Header card */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-white border rounded-xl p-4 flex items-center gap-3 md:col-span-2">
                {data.user.photo_url
                  ? <img src={data.user.photo_url} alt="" className="w-14 h-14 rounded-full object-cover border-2" />
                  : <div className="w-14 h-14 rounded-full bg-brand-gradient text-white flex items-center justify-center font-display font-bold text-xl">{(data.user.name || "?")[0].toUpperCase()}</div>}
                <div className="min-w-0">
                  <div className="font-display font-semibold text-lg truncate">{data.user.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{data.user.mobile}</div>
                  <div className="mt-1"><span className="chip border bg-slate-50 text-slate-700 border-slate-200 uppercase text-[10px] tracking-wider">{data.user.role}</span></div>
                </div>
              </div>
              <KpiCard testId="fp-wallet" label="Wallet" value={`₹ ${Number(data.user.wallet_balance || 0).toFixed(2)}`} accent="green" icon={Wallet} />
              <KpiCard testId="fp-leads" label="Total Leads" value={data.lead_stats.total} accent="blue" icon={ClipboardList} />
            </div>

            <Tabs defaultValue="personal">
              <TabsList className="flex-wrap h-auto">
                <TabsTrigger value="personal" data-testid="fp-tab-personal">Personal</TabsTrigger>
                <TabsTrigger value="kyc" data-testid="fp-tab-kyc">KYC</TabsTrigger>
                <TabsTrigger value="business" data-testid="fp-tab-business">Business</TabsTrigger>
                <TabsTrigger value="bank" data-testid="fp-tab-bank">Bank</TabsTrigger>
                <TabsTrigger value="wallet" data-testid="fp-tab-wallet">Wallet History</TabsTrigger>
                <TabsTrigger value="payments" data-testid="fp-tab-payments">Payments</TabsTrigger>
                <TabsTrigger value="leads" data-testid="fp-tab-leads">Leads</TabsTrigger>
                <TabsTrigger value="withdrawals" data-testid="fp-tab-withdrawals">Withdrawals</TabsTrigger>
              </TabsList>

              <TabsContent value="personal">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 bg-white border rounded-xl p-4 text-sm">
                  {[
                    ["Name", data.user.name], ["Mobile", data.user.mobile], ["Email", data.user.email],
                    ["Role", data.user.role], ["Aadhaar No", data.user.aadhaar_no], ["PAN No", data.user.pan_no],
                    ["State", data.user.state], ["District", data.user.district], ["Pin Code", data.user.pincode],
                    ["Parent", data.parent ? `${data.parent.name} (${data.parent.role})` : "—"],
                    ["Created At", (data.user.created_at || "").split("T")[0]],
                    ["Status", data.user.active === false ? "Disabled" : "Active"],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div>
                      <div className="mt-1 font-medium">{v || "—"}</div>
                    </div>
                  ))}
                  <div className="col-span-full">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Address</div>
                    <div className="mt-1 font-medium whitespace-pre-wrap">{data.user.address || "—"}</div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="kyc">
                <div className="bg-white border rounded-xl p-4 text-sm">
                  {data.kyc ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        ["Status", data.kyc.status], ["PAN", data.kyc.pan], ["Aadhaar", data.kyc.aadhaar],
                        ["Business Type", data.kyc.business_type], ["Submitted", (data.kyc.submitted_at || "").split("T")[0]],
                        ["Decided", (data.kyc.decided_at || "").split("T")[0] || "—"],
                      ].map(([k, v]) => (<div key={k}><div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div><div className="mt-1 font-medium">{v || "—"}</div></div>))}
                      <div className="col-span-full"><div className="text-[10px] uppercase tracking-widest text-slate-500">Address</div><div className="mt-1 whitespace-pre-wrap">{data.kyc.address || "—"}</div></div>
                      {data.kyc.remarks && <div className="col-span-full"><div className="text-[10px] uppercase tracking-widest text-slate-500">Remarks</div><div className="mt-1">{data.kyc.remarks}</div></div>}
                    </div>
                  ) : <div className="text-slate-500">No KYC submitted</div>}
                </div>
              </TabsContent>

              <TabsContent value="business">
                <div className="bg-white border rounded-xl p-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  {[
                    ["Business Name", data.user.business_name],
                    ["Business Type", data.kyc?.business_type],
                    ["Address", data.user.address],
                  ].map(([k, v]) => (<div key={k}><div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div><div className="mt-1 font-medium">{v || "—"}</div></div>))}
                </div>
              </TabsContent>

              <TabsContent value="bank">
                <div className="bg-white border rounded-xl p-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  {[
                    ["Bank Name", data.user.bank_name], ["Branch", data.user.branch_name],
                    ["Account Number", data.user.account_number], ["IFSC Code", data.user.ifsc_code],
                  ].map(([k, v]) => (<div key={k}><div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div><div className="mt-1 font-mono text-sm">{v || "—"}</div></div>))}
                </div>
              </TabsContent>

              <TabsContent value="wallet">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <KpiCard label="Balance" value={`₹ ${Number(data.user.wallet_balance || 0).toFixed(2)}`} accent="green" />
                  <KpiCard label="Locked" value={`₹ ${Number(data.user.wallet_locked || 0).toFixed(2)}`} accent="orange" />
                </div>
                <div className="bg-white border rounded-xl overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Note</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {data.wallet_tx.map((t) => (<TableRow key={t.id}><TableCell className="uppercase text-xs">{t.type}</TableCell><TableCell>₹ {Number(t.amount).toFixed(2)}</TableCell><TableCell>{t.note || "—"}</TableCell><TableCell className="text-xs text-slate-500">{(t.created_at || "").replace("T"," ").slice(0,16)}</TableCell></TableRow>))}
                      {data.wallet_tx.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-4">No transactions</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="payments">
                <div className="bg-white border rounded-xl overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Service</TableHead><TableHead>Amount</TableHead><TableHead>TXN ID</TableHead><TableHead>RRN</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {data.payments.map((p) => (<TableRow key={p.id}><TableCell>{p.service_name}</TableCell><TableCell>₹ {Number(p.amount).toFixed(2)}</TableCell><TableCell className="font-mono text-xs">{p.txn_id}</TableCell><TableCell className="font-mono text-xs">{p.rrn_number}</TableCell><TableCell><StatusBadge status={p.status} /></TableCell><TableCell className="text-xs text-slate-500">{p.payment_date}</TableCell></TableRow>))}
                      {data.payments.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-4">No payments</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="leads">
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
                  {[
                    ["total","Total","blue"], ["approved","Approved","green"], ["pending","Pending","orange"],
                    ["rejected","Rejected","red"], ["duplicate","Duplicate","red"], ["etb","ETB","green"],
                  ].map(([k, l, a]) => (
                    <KpiCard key={k} label={l} value={data.lead_stats[k] ?? 0} accent={a} />
                  ))}
                </div>
                <div className="bg-white border rounded-xl overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Mobile</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {data.leads.map((l) => (<TableRow key={l.id}><TableCell>{l.customer_name}</TableCell><TableCell className="font-mono text-xs">{l.customer_mobile}</TableCell><TableCell><StatusBadge status={l.status} /></TableCell><TableCell className="text-xs text-slate-500">{(l.created_at || "").split("T")[0]}</TableCell></TableRow>))}
                      {data.leads.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-4">No leads</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="withdrawals">
                <div className="bg-white border rounded-xl overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Amount</TableHead><TableHead>Method</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {data.withdrawals.map((w) => (<TableRow key={w.id}><TableCell>₹ {Number(w.amount).toFixed(2)}</TableCell><TableCell>{w.method}</TableCell><TableCell><StatusBadge status={w.status} /></TableCell><TableCell className="text-xs text-slate-500">{(w.created_at || "").split("T")[0]}</TableCell></TableRow>))}
                      {data.withdrawals.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-4">No withdrawals</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  return (
    <Routes>
      <Route index element={<Home />} />
      <Route path="users" element={<UsersPage />} />
      <Route path="categories" element={<CategoriesPage />} />
      <Route path="services" element={<ServicesPage />} />
      <Route path="leads" element={<LeadsPage />} />
      <Route path="payment-qrs" element={<PaymentQRsPage />} />
      <Route path="payments" element={<PaymentApprovalsPage />} />
      <Route path="kyc" element={<KycPage />} />
      <Route path="wallet" element={<WalletOpsPage />} />
      <Route path="targets" element={<TargetsPage />} />
      <Route path="notifications" element={<NotificationsPage />} />
      <Route path="commission" element={<CommissionSheetPage />} />
      <Route path="support" element={<SupportSettingsPage />} />
      <Route path="reports" element={<ReportsPage />} />
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}
